import React, { FC, Fragment, useCallback, useContext, useEffect, useState } from 'react';
import { Box, Grid } from '@mui/material';
import { useLazyQuery } from '@apollo/client';
import { getSupplierSearchTemplate } from 'apollo/templates/client-directory';
import { ClientType, LabClient, SupplierListFilter } from 'models/client-directory';
import { debounce } from 'lodash';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { useTranslation } from 'react-i18next';
import PageTitle from 'components/shared/page-title/page-title.component';
import Loader from 'components/shared/loader/loader.component';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import { useLabClients } from 'hooks/useLabClients';
import SupplierPageActions from 'components/suppliers/supplier-page-actions/supplier-page-actions.component';
import SuppliersList from 'components/suppliers/supplier-list/supplier-list.component';
import AddSupplierDialog from 'components/suppliers/add-supplier-dialog/add-supplier-dialog.component';

const SupplierDirectory: FC = () => {
    const { t: translate } = useTranslation(['common', 'client-directory']);
    const [dialogOpen, setDialogOpen] = useState(false);
    const savedFilters = JSON.parse(localStorage.getItem('supplierListFilter') || '{}');
    const [filter, setFilter] = useState<SupplierListFilter>(savedFilters as SupplierListFilter);
    const [suppliers, setSuppliers] = useState<LabClient[]>([]);
    const { showBoundary } = useDenthubErrorBoundary();
    const { setAppToasterContent } = useContext(AppToasterContext);
    const { getLabClients, fetchMoreLabClients, refetchLabClients, isLabClientListLoading } = useLabClients(
        setSuppliers,
        ClientType.SUPPLIER,
    );

    const [searchSuppliers, { loading: isSupplierSearchLoading }] = useLazyQuery(getSupplierSearchTemplate, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'network-only',
        onCompleted({ supplierSearch }) {
            setSuppliers(supplierSearch || []);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    useEffect(() => {
        const isEmptyObject = Object.keys(filter).length === 0;
        const isInitialFilter = !filter.freeText && filter.active === true;
        const isFilterActive = !!filter.freeText || !filter.active;
        if (isEmptyObject || isInitialFilter) {
            getLabClients({
                variables: {
                    offset: 0,
                    limit: 20,
                },
            });
        } else if (isFilterActive) {
            searchSuppliers({
                variables: {
                    filters: filter,
                },
            });
        }
    }, [filter]);

    const handleFilterChange = useCallback(
        debounce(({ freeText, active }: SupplierListFilter) => {
            const listFilters = { freeText: freeText, active: active };
            setFilter(listFilters);
            localStorage.setItem('supplierListFilter', JSON.stringify(listFilters));
        }, 300),
        [],
    );

    const handleOpenDialog = () => {
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
    };

    const handleSupplierAdded = () => {
        setAppToasterContent({
            isOpen: true,
            severity: 'success',
            message: translate('client-directory:SUPPLIER_ADDED'),
        });
        refetchLabClients();
    };

    return (
        <Fragment>
            <SupplierPageActions
                supplierListFilters={filter}
                onFilterChange={handleFilterChange}
                onSupplierDialogOpen={handleOpenDialog}
            />
            {isLabClientListLoading || isSupplierSearchLoading ? (
                <Loader />
            ) : (
                <SuppliersList filteredSuppliers={suppliers} onFetchMoreChange={fetchMoreLabClients} />
            )}
            <AddSupplierDialog
                dialogOpen={dialogOpen}
                onCloseDialog={handleCloseDialog}
                onSupplierAdded={handleSupplierAdded}
            />
        </Fragment>
    );
};

export default SupplierDirectory;
